"use client";

/**
 * Sequence detail data hook (Phase H · Task 51).
 *
 * Thin wrapper over Orval's `useGetSequence` that:
 *  - Disables the query until both `jobId` and `sequenceId` are present.
 *  - Surfaces a derived `stepCount` so callers don't repeatedly
 *    null-check `current_step_set?.steps`.
 *  - Re-exports the response object as-is (no shape narrowing) so the
 *    Pydantic schema stays the source of truth.
 */

import { useMemo } from "react";

import { useGetSequence } from "@/lib/api/generated";

interface UseSequenceArgs {
  jobId: string | null | undefined;
  sequenceId: string | null | undefined;
}

export function useSequence({ jobId, sequenceId }: UseSequenceArgs) {
  const enabled = Boolean(jobId && sequenceId);
  const query = useGetSequence(jobId ?? "", sequenceId ?? "", {
    query: { enabled },
  });

  const stepCount = useMemo(() => {
    return query.data?.current_step_set?.steps?.length ?? 0;
  }, [query.data?.current_step_set?.steps?.length]);

  return {
    sequence: query.data ?? null,
    stepCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    queryKey: query.queryKey,
  };
}
